/**
 * FinancialStatementsSection
 * 
 * Search result section for Financial Statements (EX2)
 * Displays matching line items across all categories (Assets, Liabilities, Equity, Revenue, Expenses)
 * 
 * NOTE: This is a display-only component. Data is fetched by useGlobalSearch hook.
 * Styling matches exactly with FinancialStatementsPage.tsx
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Collapse, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import styled from 'styled-components';
import { LineItem, parseBackendLineItem } from '../../../../services/financialStatementService';
import { CycleItem, ProcessItem } from '../../../../services/hierarchyService';
import HighlightText from '../components/HighlightText';
import { FinancialStatementsResult } from '../useGlobalSearch';

const { Panel } = Collapse;

// ========================================
// Types - Match parsed format from parseBackendLineItem
// ========================================

interface ParsedLineItem {
  id: string;
  name: string;
  amount: string;
  isImmaterial: boolean;
  cycles: CycleItem[];
  processes: ProcessItem[];
  index: number;
  createdAt: string;
}

// ========================================
// Styled Components - Match FinancialStatementsPage.tsx exactly
// ========================================

const SectionContainer = styled.div`
  margin-bottom: 24px;
`;

const SectionHeader = styled.div`
  background: #E2EBFF;
  padding: 12px 16px;
  border-radius: 8px 8px 0 0;
  border: 1px solid #C7D2FE;
  border-bottom: none;
`;

const SectionTitle = styled.h3`
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  color: #3730A3;
  margin: 0;
`;

const ContentContainer = styled.div`
  background: white;
  border: 1px solid #E5E7EB;
  border-radius: 0 0 8px 8px;
  overflow: hidden;
`;

const SubSectionTitle = styled.span`
  font-family: Inter;
  font-weight: 500;
  font-size: 13px;
  color: #374151;
`;

const SubSectionCount = styled.span`
  font-family: Inter;
  font-weight: 400;
  font-size: 13px;
  color: #6B7280;
  margin-left: 8px;
`;

// Table wrapper matching GridWrapper from original
const TableWrapper = styled.div`
  .ant-table {
    border-radius: 0;
  }

  .ant-table-thead > tr > th {
    background: #F8FAFC;
    font-family: Inter;
    font-weight: 500;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0.005em;
    color: #6B7280;
    text-transform: uppercase;
    padding: 12px 16px;
    border-bottom: 1px solid #E5E7EB;
    border-right: 1px solid #E5E7EB;
    
    &:last-child {
      border-right: none;
    }
    
    /* Remove Ant Design's internal column separators */
    &::before {
      display: none !important;
    }
  }

  .ant-table-tbody > tr > td {
    font-family: Inter;
    font-size: 14px;
    color: #1F2937;
    padding: 12px 16px;
    border-bottom: 1px solid #E5E7EB;
    border-right: 1px solid #E5E7EB;
    
    &:last-child {
      border-right: none;
    }
  }

  .ant-table-tbody > tr:last-child > td {
    border-bottom: none;
  }

  .ant-table-tbody > tr:hover > td {
    background: #F9FAFB;
  }
`;

const StyledCollapse = styled(Collapse)`
  border: none;
  background: white;

  .ant-collapse-item {
    border-bottom: 1px solid #E5E7EB;

    &:last-child {
      border-bottom: none;
    }
  }

  .ant-collapse-header {
    padding: 12px 16px !important;
    background: #F8FAFC;
  }

  .ant-collapse-content-box {
    padding: 0 !important;
  }
`;

// Tag styling - Match exactly from FinancialStatementsPage.tsx
const CycleTag = styled.span`
  display: inline-block;
  padding: 4px 12px;
  background: #ECFDF5;
  color: #115E59;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.3s;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  flex-shrink: 0;

  &:hover {
    opacity: 0.8;
    transform: translateY(-1px);
  }
`;

const ProcessTag = styled.span`
  display: inline-block;
  padding: 4px 12px;
  background: #EFF6FF;
  color: #1E40AF;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.3s;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  flex-shrink: 0;

  &:hover {
    opacity: 0.8;
    transform: translateY(-1px);
  }
`;

const ImmaterialTag = styled.span`
  display: inline-block;
  padding: 4px 12px;
  background: #F3F4F6;
  color: #1F2937;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  white-space: nowrap;
`;

const EmptyCell = styled.span`
  color: #9CA3AF;
`;

// Tooltip content styling - Match exactly from FinancialStatementsPage.tsx
const TooltipTitle = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #1F2937;
  text-align: center;
  margin-bottom: 12px;
`;

const TooltipList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TooltipItem = styled.div`
  padding: 8px 12px;
  background: #EFF6FF;
  border-radius: 6px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #2563EB;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  white-space: normal;
  word-break: break-word;

  &:hover {
    background: #DBEAFE;
    transform: translateX(2px);
  }
`;

const CycleTooltipItem = styled(TooltipItem)`
  background: #ECFDF5;
  color: #115E59;
  
  &:hover {
    background: #D1FAE5;
  }
`;

// ========================================
// Types
// ========================================

export interface FinancialStatementsSectionProps {
  data: FinancialStatementsResult;
  searchText: string;
}

interface CategoryData {
  key: string;
  name: string;
  items: ParsedLineItem[];
}

// ========================================
// Component
// ========================================

const FinancialStatementsSection: React.FC<FinancialStatementsSectionProps> = ({
  data,
  searchText,
}) => {
  const navigate = useNavigate();

  // Parse raw LineItem[] to ParsedLineItem[] using the same function as original page
  const parseItems = (items: LineItem[]): ParsedLineItem[] => {
    return items.map(item => parseBackendLineItem(item));
  };

  // Build categories from data with parsed items
  const categories: CategoryData[] = [];
  
  if (data.assets.length > 0) {
    categories.push({ key: 'assets', name: 'Balance Sheet - Assets', items: parseItems(data.assets) });
  }
  if (data.liabilities.length > 0) {
    categories.push({ key: 'liabilities', name: 'Balance Sheet - Liabilities', items: parseItems(data.liabilities) });
  }
  if (data.equity.length > 0) {
    categories.push({ key: 'equity', name: 'Balance Sheet - Equity', items: parseItems(data.equity) });
  }
  if (data.revenue.length > 0) {
    categories.push({ key: 'revenue', name: 'Income Statement - Revenue', items: parseItems(data.revenue) });
  }
  if (data.expenses.length > 0) {
    categories.push({ key: 'expenses', name: 'Income Statement - Expenses', items: parseItems(data.expenses) });
  }

  // Navigation handlers - Match FinancialStatementsPage.tsx
  const handleCycleClick = (cycleId: string) => {
    navigate('/process-hierarchy', { state: { selectedCycleId: cycleId } });
  };

  const handleProcessClick = (processId: string) => {
    navigate('/process-hierarchy', { state: { selectedProcessId: processId } });
  };

  // Render cycles column - Match cycleTemplate from FinancialStatementsPage.tsx
  const renderCycles = (record: ParsedLineItem) => {
    // Show Immaterial tag if line item is marked as immaterial
    if (record.isImmaterial) {
      return <ImmaterialTag>Immaterial</ImmaterialTag>;
    }

    if (!record.cycles || record.cycles.length === 0) {
      return <EmptyCell>-</EmptyCell>;
    }

    // Tooltip content for multiple cycles
    const tooltipContent = record.cycles.length > 1 ? (
      <div>
        <TooltipTitle>Cycles</TooltipTitle>
        <TooltipList>
          {record.cycles.map((cycle, index) => (
            <CycleTooltipItem
              key={index}
              onClick={() => handleCycleClick(cycle.cycleId)}
            >
              <HighlightText text={`${cycle.cycleCode} ${cycle.cycleName}`} searchText={searchText} />
            </CycleTooltipItem>
          ))}
        </TooltipList>
      </div>
    ) : null;

    const cycleContent = (
      <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '4px', overflow: 'hidden' }}>
        {record.cycles.map((cycle, index) => (
          <CycleTag
            key={index}
            onClick={() => handleCycleClick(cycle.cycleId)}
          >
            <HighlightText text={`${cycle.cycleCode} ${cycle.cycleName}`} searchText={searchText} />
          </CycleTag>
        ))}
      </div>
    );

    // Show tooltip only if multiple cycles
    if (tooltipContent) {
      return (
        <Tooltip
          title={tooltipContent}
          placement="top"
          overlayStyle={{ maxWidth: '400px' }}
          overlayInnerStyle={{
            backgroundColor: '#FFFFFF',
            padding: '16px',
            borderRadius: '12px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
          }}
          color="#FFFFFF"
        >
          {cycleContent}
        </Tooltip>
      );
    }

    return cycleContent;
  };

  // Render processes column - Match processTemplate from FinancialStatementsPage.tsx
  const renderProcesses = (record: ParsedLineItem) => {
    // Show Immaterial tag if line item is marked as immaterial
    if (record.isImmaterial) {
      return <ImmaterialTag>Immaterial</ImmaterialTag>;
    }

    if (!record.processes || record.processes.length === 0) {
      return <EmptyCell>-</EmptyCell>;
    }

    // Tooltip content for multiple processes
    const tooltipContent = record.processes.length > 1 ? (
      <div>
        <TooltipTitle>Processes</TooltipTitle>
        <TooltipList>
          {record.processes.map((process, index) => (
            <TooltipItem
              key={index}
              onClick={() => handleProcessClick(process.processId)}
            >
              <HighlightText text={`${process.processCode} ${process.processName}`} searchText={searchText} />
            </TooltipItem>
          ))}
        </TooltipList>
      </div>
    ) : null;

    const processContent = (
      <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '4px', overflow: 'hidden' }}>
        {record.processes.map((process, index) => (
          <ProcessTag
            key={index}
            onClick={() => handleProcessClick(process.processId)}
          >
            <HighlightText text={`${process.processCode} ${process.processName}`} searchText={searchText} />
          </ProcessTag>
        ))}
      </div>
    );

    // Show tooltip only if multiple processes
    if (tooltipContent) {
      return (
        <Tooltip
          title={tooltipContent}
          placement="top"
          overlayStyle={{ maxWidth: '400px' }}
          overlayInnerStyle={{
            backgroundColor: '#FFFFFF',
            padding: '16px',
            borderRadius: '12px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
          }}
          color="#FFFFFF"
        >
          {processContent}
        </Tooltip>
      );
    }

    return processContent;
  };

  // Table columns - Match column structure from FinancialStatementsPage.tsx
  const columns: ColumnsType<ParsedLineItem> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => (
        <HighlightText text={name} searchText={searchText} />
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 200,
    },
    {
      title: 'Cycle',
      key: 'cycles',
      width: 264,
      render: (_, record) => renderCycles(record),
    },
    {
      title: 'Process',
      key: 'processes',
      width: 300,
      render: (_, record) => renderProcesses(record),
    },
  ];

  // Calculate total results
  const totalResults = data.assets.length + data.liabilities.length + 
                       data.equity.length + data.revenue.length + data.expenses.length;

  // Don't render if no results
  if (categories.length === 0) {
    return null;
  }

  return (
    <SectionContainer>
      <SectionHeader>
        <SectionTitle>Financial Statements ({totalResults} results)</SectionTitle>
      </SectionHeader>
      
      <ContentContainer>
        <StyledCollapse defaultActiveKey={categories.map(c => c.key)}>
          {categories.map((category) => (
            <Panel
              key={category.key}
              header={
                <>
                  <SubSectionTitle>{category.name}</SubSectionTitle>
                  <SubSectionCount>({category.items.length})</SubSectionCount>
                </>
              }
            >
              <TableWrapper>
                <Table
                  columns={columns}
                  dataSource={category.items}
                  rowKey="id"
                  pagination={false}
                  size="small"
                />
              </TableWrapper>
            </Panel>
          ))}
        </StyledCollapse>
      </ContentContainer>
    </SectionContainer>
  );
};

export default FinancialStatementsSection;
